import { useCallback, useEffect, useRef, useState } from 'react';

/** Quiet time after the last edit before an autosave (ms). */
export const AUTOSAVE_DELAY_MS = 1500;

/**
 * Autosave for the deck template editor.
 *
 * The page describes everything a save writes as one string (`snapshot`). Saves run one at a
 * time: a save asked for while one runs waits for it, then writes only if there are still unsaved
 * edits. `write(snapshot, options)` does the actual write (with expectedUpdatedAt optimistic
 * concurrency) and resolves to `{ok: true}`, `{conflict: true, data}` (the row changed elsewhere;
 * autosave stops until the user decides) or `{ok: false, error}` (autosave pauses until the next
 * edit or a manual save).
 *
 * @param {object} args - Arguments.
 * @param {string} args.snapshot - Current state as a comparable string.
 * @param {function(string, object): Promise<object>} args.write - Write a snapshot.
 * @param {boolean} [args.enabled] - Autosave at all (the explicit save still works).
 * @param {number} [args.delayMs] - Debounce.
 * @returns {{status: 'saved'|'dirty'|'saving'|'error'|'conflict', error: string, conflict: object|null, lastSavedAt: number|null, isDirty: function(): boolean, saveNow: function(object=): Promise<boolean>, markSaved: function(string): void, clearConflict: function(): void}} Autosave state.
 */
export function useDeckAutosave({ snapshot, write, enabled = true, delayMs = AUTOSAVE_DELAY_MS }) {
  const snapshotRef = useRef(snapshot);
  snapshotRef.current = snapshot;
  const writeRef = useRef(write);
  writeRef.current = write;
  const baselineRef = useRef(snapshot);
  const runningRef = useRef(null);
  /** Snapshot whose write failed: autosave waits for a different one. */
  const failedRef = useRef(null);
  const conflictRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [conflict, setConflict] = useState(null);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [, setTick] = useState(0);

  const isDirty = useCallback(() => snapshotRef.current !== baselineRef.current, []);

  const markSaved = useCallback((value) => {
    baselineRef.current = value;
    failedRef.current = null;
    setError('');
    setTick((n) => n + 1);
  }, []);

  const clearConflict = useCallback(() => {
    conflictRef.current = null;
    setConflict(null);
  }, []);

  const saveNow = useCallback(async (options = {}) => {
    while (runningRef.current) {
      await runningRef.current.catch(() => false);
    }
    if (conflictRef.current && !options.force) return false;
    if (!options.always && !isDirty()) return true;
    const value = snapshotRef.current;
    const attempt = (async () => {
      setSaving(true);
      try {
        const result = await writeRef.current(value, options);
        if (result?.conflict) {
          conflictRef.current = result.data || {};
          setConflict(result.data || {});
          return false;
        }
        if (!result?.ok) {
          failedRef.current = value;
          setError(result?.error || 'The template could not be saved.');
          return false;
        }
        conflictRef.current = null;
        setConflict(null);
        baselineRef.current = value;
        failedRef.current = null;
        setError('');
        setLastSavedAt(Date.now());
        return snapshotRef.current === value;
      } catch (err) {
        failedRef.current = value;
        setError(err?.message || 'The template could not be saved.');
        return false;
      } finally {
        setSaving(false);
      }
    })();
    runningRef.current = attempt;
    try {
      return await attempt;
    } finally {
      if (runningRef.current === attempt) runningRef.current = null;
    }
  }, [isDirty]);

  // Debounced autosave after edits.
  useEffect(() => {
    if (!enabled || conflict || snapshot === baselineRef.current || snapshot === failedRef.current) return undefined;
    const timer = setTimeout(() => {
      saveNow();
    }, delayMs);
    return () => clearTimeout(timer);
  }, [snapshot, enabled, conflict, delayMs, saveNow, saving]);

  let status = 'saved';
  if (conflict) status = 'conflict';
  else if (saving) status = 'saving';
  else if (error && snapshot === failedRef.current) status = 'error';
  else if (snapshot !== baselineRef.current) status = 'dirty';

  return { status, error, conflict, lastSavedAt, isDirty, saveNow, markSaved, clearConflict };
}

export default useDeckAutosave;
